import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { getTasks, updateTask, createTask, deleteTask } from '../../services/tasksAPI';
import { Search, Plus, Clock, AlertCircle, Trash2, CheckCircle2 } from 'lucide-react';
import { useNotification } from '../../context/NotificationContext';
import { useModal } from '../../context/ModalContext';
import TaskModal from '../../features/tasks/components/TaskModal';
import MiniLoader from '../../components/MiniLoader';
import PageAnimator from '../../components/ui/PageAnimator';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/Badge';
import Input from '../../components/ui/Input';

const STATUSES = {
  TODO: { id: 'TODO', title: 'À faire', color: 'bg-surface', borderColor: 'border-[var(--hp-color-border-muted)]', headerColor: 'text-primary' },
  IN_PROGRESS: { id: 'IN_PROGRESS', title: 'En cours', color: 'bg-surface', borderColor: 'border-[var(--hp-info-200)]', headerColor: 'text-[var(--hp-info-700)]' },
  DONE: { id: 'DONE', title: 'Terminé', color: 'bg-surface', borderColor: 'border-[var(--hp-success-200)]', headerColor: 'text-[var(--hp-success-700)]' }
};

const PRIORITIES = {
  LOW: { label: 'Basse', variant: 'neutral' },
  MEDIUM: { label: 'Moyenne', variant: 'info' },
  HIGH: { label: 'Haute', variant: 'warning' },
  CRITICAL: { label: 'Critique', variant: 'destructive' }
};

const ToDoList = () => {
  const { showToast } = useNotification();
  const [tasks, setTasks] = useState({ TODO: [], IN_PROGRESS: [], DONE: [] });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  
  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const data = await getTasks();
      const grouped = { TODO: [], IN_PROGRESS: [], DONE: [] };
      data.forEach(task => {
        if (grouped[task.status]) {
          grouped[task.status].push(task);
        }
      });
      setTasks(grouped);
    } catch (error) {
      showToast("Erreur lors du chargement des tâches", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const sourceStatus = source.droppableId;
    const destStatus = destination.droppableId;

    const sourceColumn = [...tasks[sourceStatus]];
    const destColumn = sourceStatus === destStatus ? sourceColumn : [...tasks[destStatus]];

    const [movedTask] = sourceColumn.splice(source.index, 1);
    
    // Mise à jour optimiste
    if (sourceStatus === destStatus) {
      sourceColumn.splice(destination.index, 0, movedTask);
      setTasks({ ...tasks, [sourceStatus]: sourceColumn });
    } else {
      movedTask.status = destStatus;
      destColumn.splice(destination.index, 0, movedTask);
      setTasks({
        ...tasks,
        [sourceStatus]: sourceColumn,
        [destStatus]: destColumn
      });
    }

    if (sourceStatus !== destStatus) {
      try {
        await updateTask(draggableId, { status: destStatus });
        showToast("Statut mis à jour", "success");
      } catch (error) {
        showToast("Erreur de sauvegarde", "error");
        fetchTasks(); 
      }
    }
  };

  const { confirm } = useModal();

  const handleDelete = async (id) => {
    const isConfirmed = await confirm({
      title: "Supprimer la tâche",
      message: "Voulez-vous vraiment supprimer cette tâche ?",
      type: "danger",
      confirmText: "Supprimer",
      cancelText: "Annuler"
    });
    if (!isConfirmed) return;
    try {
      await deleteTask(id);
      showToast("Tâche supprimée", "success");
      fetchTasks();
    } catch (error) {
      showToast("Erreur lors de la suppression", "error");
    }
  };

  const handleSaveTask = async (taskData, id) => {
    if (id) {
      await updateTask(id, taskData);
      showToast("Tâche mise à jour", "success");
    } else {
      await createTask(taskData);
      showToast("Tâche créée", "success");
    }
    fetchTasks();
  };

  const TaskCard = ({ task, index }) => (
    <Draggable draggableId={task.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={() => { setSelectedTask(task); setIsModalOpen(true); }}
          className={`bg-surface p-4 rounded-xl shadow-hp-sm border border-[var(--hp-color-border-muted)] mb-3 group
            transition-all duration-200 ease-in-out hover:shadow-hp-md hover:-translate-y-0.5 cursor-pointer
            ${snapshot.isDragging ? 'shadow-hp-lg ring-2 ring-primary ring-opacity-50 rotate-1' : ''}`}
        >
          <div className="flex justify-between items-start mb-2">
            <Badge variant={PRIORITIES[task.priority]?.variant || 'neutral'}>
              {PRIORITIES[task.priority]?.label || task.priority}
            </Badge>
            <Button 
              variant="ghost"
              size="icon"
              onClick={(e) => { e.stopPropagation(); handleDelete(task.id); }} 
              className="h-7 w-7 text-subtle hover:text-[var(--hp-danger-600)] opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          
          <h4 className="font-semibold text-primary mb-1 leading-tight">{task.title}</h4>
          {task.description && (
            <p className="text-sm text-subtle line-clamp-2 mb-3">{task.description}</p>
          )}
          
          <div className="space-y-2 mt-3">
            {task.operationCase && (
              <div className="flex items-center text-xs text-secondary bg-surface-muted px-2 py-1 rounded-md w-fit border border-[var(--hp-color-border-muted)]">
                <AlertCircle className="w-3.5 h-3.5 mr-1" /> Dossier: {task.operationCase.title}
              </div>
            )}
            
            <div className="flex items-center justify-between text-xs text-subtle pt-2 border-t border-[var(--hp-color-border-muted)]">
              <div className="flex items-center">
                <Clock className="w-3.5 h-3.5 mr-1" />
                {task.dueAt ? new Date(task.dueAt).toLocaleDateString('fr-FR') : 'Pas d\'échéance'}
              </div>
              <div className="flex items-center" title={task.assignedMembership?.user?.displayName || task.assignedMembership?.userAccount?.displayName || 'Non assigné'}>
                <div className="w-6 h-6 rounded-full bg-secondary text-inverse flex items-center justify-center text-[10px] font-bold shadow-hp-sm">
                  {(task.assignedMembership?.user?.displayName || task.assignedMembership?.userAccount?.displayName || '?').charAt(0).toUpperCase()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );

  return (
    <div className="p-4 sm:p-8 bg-workspace min-h-screen flex flex-col">
      <PageAnimator>
        {/* Header Premium Standardisé */}
        <div
          className="
            relative z-20
            flex shrink-0 flex-col
            gap-6
            overflow-visible
            workspace-card
            bg-cover bg-center bg-no-repeat
            p-4 sm:p-6
            mb-6
            xl:flex-row xl:items-center xl:justify-between
          "
          style={{ backgroundImage: "url('/assets/backgrounds/lighthouse_bg.png')" }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 rounded-[var(--hp-radius-lg)] bg-overlay"
          />

          <div className="relative z-10 flex min-w-0 shrink-0 flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-secondary text-inverse shadow-hp-sm">
              <CheckCircle2 className="h-6 w-6" />
            </div>

            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-inverse">
                Centre de Tâches
              </h1>
              <p className="mt-1 text-sm text-inverse opacity-80">
                Gérez l'avancement et les priorités de toutes vos opérations.
              </p>
            </div>
          </div>

          <div className="relative z-10 flex flex-col sm:flex-row items-center gap-3 w-full xl:w-auto">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-subtle pointer-events-none" size={16} />
              <Input 
                type="text"
                placeholder="Rechercher une tâche..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-full"
              />
            </div>
            <Button 
              variant="accent"
              onClick={() => { setSelectedTask(null); setIsModalOpen(true); }}
              className="whitespace-nowrap shadow-hp-sm w-full sm:w-auto"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Nouvelle Tâche
            </Button>
          </div>
        </div>

        {/* Kanban Board */}
        {loading ? (
          <MiniLoader />
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="flex-1 flex gap-6 overflow-x-auto pb-4">
              {Object.values(STATUSES).map(status => (
                <div key={status.id} className={`flex flex-col w-[350px] min-w-[350px] rounded-2xl border ${status.borderColor} workspace-card-elevated overflow-hidden`}>
                  <div className="px-5 py-4 border-b border-[var(--hp-color-border-muted)] bg-surface/60 backdrop-blur-sm flex justify-between items-center">
                    <h3 className={`font-semibold ${status.headerColor} flex items-center gap-2`}>
                      {status.title}
                      <span className="bg-surface-muted border border-[var(--hp-color-border-muted)] text-xs py-0.5 px-2 rounded-full font-medium text-subtle">
                        {tasks[status.id].length}
                      </span>
                    </h3>
                  </div>
                  
                  <Droppable droppableId={status.id}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`flex-1 p-4 overflow-y-auto transition-colors min-h-[400px] ${snapshot.isDraggingOver ? 'bg-surface-muted/50' : ''}`}
                      >
                        {tasks[status.id]
                          .filter(t => t.title.toLowerCase().includes(searchTerm.toLowerCase()))
                          .map((task, index) => (
                          <TaskCard key={task.id} task={task} index={index} />
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              ))}
            </div>
          </DragDropContext>
        )}

        <TaskModal 
          isOpen={isModalOpen}
          onClose={() => { setIsModalOpen(false); setSelectedTask(null); }}
          task={selectedTask}
          onSave={handleSaveTask}
        />
      </PageAnimator>
    </div>
  );
};

export default ToDoList;
